import * as React from 'react'
import { PaymentNudge as PaymentNudgeBase } from '../payment-nudge'
import { useUserPromoPopupData as useUserPromoPopupDataBase } from './use-user-promo-data'
import { PromoPopupLoggedInTrial as PromoPopupLoggedInTrialBase } from './types/logged-in-trial-banner'
import { PromoPopupLoggedInYearly as PromoPopupLoggedInYearlyBase } from './types/logged-in-yearly-banner'
import { PromoPopupLoggedInUnsubscribed as PromoPopupLoggedInUnsubscribedBase } from './types/logged-in-unsubscribed-banner'
import { shouldShowPopup as shouldShowPopupBase } from './utils'
import { FREE_TRIAL_ELIGIBLE_AGAIN_SEARCH_PARAM } from 'constants/search-params'

export interface PromoPopupsDependencies {
  PaymentNudge: typeof PaymentNudgeBase
  PromoPopupLoggedInTrial: typeof PromoPopupLoggedInTrialBase
  PromoPopupLoggedInUnsubscribed: typeof PromoPopupLoggedInUnsubscribedBase
  PromoPopupLoggedInYearly: typeof PromoPopupLoggedInYearlyBase
  shouldShowPopup: typeof shouldShowPopupBase
  useUserPromoPopupData: typeof useUserPromoPopupDataBase
}

const defaultPromoPopupsDependencies: PromoPopupsDependencies = {
  PaymentNudge: PaymentNudgeBase,
  PromoPopupLoggedInTrial: PromoPopupLoggedInTrialBase,
  PromoPopupLoggedInUnsubscribed: PromoPopupLoggedInUnsubscribedBase,
  PromoPopupLoggedInYearly: PromoPopupLoggedInYearlyBase,
  shouldShowPopup: shouldShowPopupBase,
  useUserPromoPopupData: useUserPromoPopupDataBase,
}

export function PromoPopups({
  dependencies = defaultPromoPopupsDependencies,
}: {
  dependencies?: PromoPopupsDependencies
} = {}) {
  const {
    PaymentNudge,
    PromoPopupLoggedInTrial,
    PromoPopupLoggedInUnsubscribed,
    PromoPopupLoggedInYearly,
    shouldShowPopup,
    useUserPromoPopupData,
  } = dependencies
  const [isOpen, setIsOpen] = React.useState(false)
  const {
    isPending,
    loggedInUnsubscribedPromo,
    loggedInTrialPromo,
    loggedInYearlyPromo,
    stripeCustomerId,
  } = useUserPromoPopupData()
  const hasFreeTrialEligibleAgainParam =
    typeof window !== 'undefined' &&
    new URL(window.location.href).searchParams.get(FREE_TRIAL_ELIGIBLE_AGAIN_SEARCH_PARAM) ===
      'true'
  const shouldShowLoggedInUnsubscribedPromo =
    !isPending &&
    !hasFreeTrialEligibleAgainParam &&
    loggedInUnsubscribedPromo &&
    shouldShowPopup('LOGGED_IN_UNSUBSCRIBED')
  const shouldShowLoggedInTrialPromo =
    !isPending &&
    !hasFreeTrialEligibleAgainParam &&
    loggedInTrialPromo &&
    shouldShowPopup('LOGGED_IN_TRIAL')
  const shouldShowLoggedInYearlyPromo =
    !isPending &&
    !hasFreeTrialEligibleAgainParam &&
    loggedInYearlyPromo &&
    shouldShowPopup('LOGGED_IN_YEARLY')
  if (hasFreeTrialEligibleAgainParam) {
    return null
  }

  if (isPending) {
    return null
  }

  if (shouldShowLoggedInUnsubscribedPromo) {
    return <PromoPopupLoggedInUnsubscribed isOpen={isOpen} setIsOpen={setIsOpen} />
  }

  if (shouldShowLoggedInTrialPromo) {
    return (
      <PromoPopupLoggedInTrial
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        stripeCustomerId={stripeCustomerId}
      />
    )
  }

  if (shouldShowLoggedInYearlyPromo) {
    return (
      <PromoPopupLoggedInYearly
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        stripeCustomerId={stripeCustomerId}
      />
    )
  }

  return <PaymentNudge />
}
